import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Signal,
  ViewChild,
  computed,
  inject,
} from '@angular/core';
import { DropdownOverlayService } from '@app/components/dropdown-menu/dropdown-overlay.service';
import type {
  DownloadActions,
  MenuItemInput,
} from '@app/models/document.model';
import { ToastService } from '@app/services/toast.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { ICellRendererAngularComp } from 'ag-grid-angular';
import type { ICellRendererParams } from 'ag-grid-community';
import { catchError, of } from 'rxjs';

type RendererParams = ICellRendererParams & { actions?: DownloadActions };

@Component({
  selector: 'app-download-cell-renderer',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonComponent],
  templateUrl: './download-cell-renderer.component.html',
  styleUrl: './download-cell-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DownloadCellRendererComponent implements ICellRendererAngularComp {
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  private readonly menu = inject(DropdownOverlayService);

  @ViewChild('btn', { static: true }) btn!: ElementRef<HTMLButtonElement>;

  private actions?: DownloadActions;

  agInit(params: RendererParams): void {
    this.actions = params.actions ?? this.actions;
  }

  refresh(): boolean {
    return false;
  }

  readonly isLoading: Signal<boolean> = computed(
    () => this.actions?.loadingSignal?.() ?? this.actions?.loading?.() ?? false
  );

  onOpen(event: MouseEvent): void {
    if (!this.actions?.fetchList) return;

    const target =
      (event.currentTarget as HTMLElement) ?? this.btn?.nativeElement;
    const rect = target?.getBoundingClientRect();
    if (!rect) return;

    const point = {
      x: Math.round(rect.left + rect.width / 2),
      y: Math.round(rect.bottom),
    };

    this.actions
      .fetchList()
      .pipe(
        catchError(() => {
          this.toast.error(
            this.translate.instant('admin.toasts.documents.list.error')
          );
          return of<MenuItemInput[]>([]);
        })
      )
      .subscribe((items: MenuItemInput[]) => {
        const menuItems = Array.isArray(items) ? items : [];
        this.menu.openAtPoint(point, menuItems, (pickedKey: string) => {
          const obs = this.actions?.download?.(pickedKey);
          obs?.subscribe({
            next: () =>
              this.toast.success(
                this.translate.instant(
                  'admin.toasts.documents.download.success'
                )
              ),
            error: () =>
              this.toast.error(
                this.translate.instant('admin.toasts.documents.download.error')
              ),
          });
        });
      });
  }
}
